import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation, Trans } from 'react-i18next';
import {
    Send,
    Users,
    LayoutTemplate,
    BarChart3,
    FileText,
    ShieldCheck,
    Truck,
    Wrench,
    Hotel,
    Monitor,
    Plus,
    Home,
    BookOpen,
    ArrowRight,
    Car,
    Package,
    HardHat,
    Sparkles,
    ClipboardCheck,
    ClipboardList,
    CheckCircle2
} from 'lucide-react';
import './Landing.css';
import LandingAuth from '../components/LandingAuth';
import LanguageSwitcher from '../components/LanguageSwitcher';

const Landing = () => {
    const navigate = useNavigate();
    const { login, register } = useAuth();
    const { t } = useTranslation();
    const [openFaq, setOpenFaq] = useState(null);
    const [showHeroAuth, setShowHeroAuth] = useState(false);
    const authSlideoutRef = useRef(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const handleStartClick = () => {
        if (!showHeroAuth) {
            setShowHeroAuth(true);
            // Wait for transition to start/render
            setTimeout(() => {
                if (authSlideoutRef.current) {
                    authSlideoutRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            }, 300); // Increased delay slightly to allow CSS transition to initiate expansion
        } else {
            if (authSlideoutRef.current) {
                authSlideoutRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        }
    };

    const faqs = [
        {
            question: t('faq.q1.question'),
            answer: t('faq.q1.answer')
        },
        {
            question: t('faq.q2.question'),
            answer: t('faq.q2.answer')
        },
        {
            question: t('faq.q3.question'),
            answer: t('faq.q3.answer')
        },
        {
            question: t('faq.q4.question'),
            answer: t('faq.q4.answer')
        },
        {
            question: t('faq.q5.question'),
            answer: t('faq.q5.answer')
        },
        {
            question: t('faq.q6.question'),
            answer: t('faq.q6.answer')
        },
        {
            question: t('faq.q7.question'),
            answer: t('faq.q7.answer')
        },
        {
            question: t('faq.q8.question'),
            answer: t('faq.q8.answer')
        },
        {
            question: t('faq.q9.question'),
            answer: t('faq.q9.answer')
        },
        {
            question: t('faq.q10.question'),
            answer: t('faq.q10.answer')
        }
    ];

    const PLANS = {
        free: {
            name: 'Free',
            price: 0,
            statusLimit: 10,
            description: t('pricing.plans.free.description'),
            features: [
                t('pricing.plans.free.features.0'),
                t('pricing.plans.free.features.1'),
                t('pricing.plans.free.features.2'),
                t('pricing.plans.free.features.3')
            ]
        },
        starter: {
            name: 'Starter',
            price: 49,
            statusLimit: 100,
            description: t('pricing.plans.starter.description'),
            features: [
                t('pricing.plans.starter.features.0'),
                t('pricing.plans.starter.features.1'),
                t('pricing.plans.starter.features.2'),
                t('pricing.plans.starter.features.3'),
                t('pricing.plans.starter.features.4'),
                t('pricing.plans.starter.features.5')
            ],
            popular: false
        },
        business: {
            name: 'Business',
            price: 149,
            statusLimit: 500,
            description: t('pricing.plans.business.description'),
            features: [
                t('pricing.plans.business.features.0'),
                t('pricing.plans.business.features.1'),
                t('pricing.plans.business.features.2'),
                t('pricing.plans.business.features.3'),
                t('pricing.plans.business.features.4'),
                t('pricing.plans.business.features.5'),
                t('pricing.plans.business.features.6')
            ],
            popular: true
        }
    };

    const landingSchema = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Organization",
                "name": "SprawdzStatus.pl",
                "url": "https://sprawdzstatus.pl",
                "logo": "https://sprawdzstatus.pl/mila-papier-logo.png",
                "description": t('hero.subtitle')
            },
            {
                "@type": "WebSite",
                "name": "SprawdzStatus.pl",
                "url": "https://sprawdzstatus.pl"
            }
        ]
    };

    return (
        <div className="landing-page">
            <Helmet>
                <title>System monitorowania zleceń i śledzenia statusu zadań | SprawdzStatus.pl</title>
                <meta name="description" content="Wysyłaj unikalne linki pracownikom i podwykonawcom. Śledź statusy zleceń, zbieraj zdjęcia i dowody realizacji. Bez instalacji aplikacji. Wypróbuj za darmo." />
                <meta property="og:title" content="System monitorowania zleceń i śledzenia statusu zadań | SprawdzStatus.pl" />
                <meta property="og:description" content="Wysyłaj unikalne linki pracownikom i podwykonawcom. Śledź statusy zleceń, zbieraj zdjęcia i dowody realizacji. Bez instalacji aplikacji." />
                <meta name="twitter:title" content="System monitorowania zleceń i śledzenia statusu zadań | SprawdzStatus.pl" />
                <meta name="twitter:description" content="Wysyłaj unikalne linki pracownikom i podwykonawcom. Śledź statusy zleceń, zbieraj zdjęcia i dowody realizacji." />
            </Helmet>
            <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
                <LanguageSwitcher />
            </div>

            <main>
                <section className={`hero-section ${showHeroAuth ? 'auth-open' : ''}`}>
                <div className="hero-content">
                    <div className="hero-text">
                        <h1 className="hero-title">
                            <span className="hero-title-content">
                                <svg className="hero-icon" viewBox="0 0 24 24" fill="#F5DF4E" stroke="#F5DF4E" strokeWidth="2">
                                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                                </svg>
                                <span>
                                    System monitorowania zleceń i <span className="highlight">śledzenia statusu zadań</span>
                                </span>
                            </span>
                        </h1>
                        <p className="hero-subtitle">
                            {t('hero.subtitle')}
                        </p>
                        <div className="hero-cta">
                            <button
                                className="btn btn-primary"
                                onClick={handleStartClick}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                            >
                                <Plus size={20} />
                                {t('hero.cta.start')}
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                            >
                                {t('hero.cta.howItWorks')}
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={() => navigate('/blog')}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                            >
                                <BookOpen size={20} />
                                Blog
                            </button>
                        </div>

                        <div
                            ref={authSlideoutRef}
                            className={`hero-auth-slideout ${showHeroAuth ? 'open' : ''}`}
                        >
                            <div className="slideout-content">
                                <LandingAuth idSuffix="-hero" />
                            </div>
                        </div>
                    </div>
                    <div className="hero-image">
                        <picture>
                             <source srcSet="/hero-image.webp" type="image/webp" />
                             <img src="/hero-image.png" alt="Zespół korzystający z systemu SprawdzStatus.pl do śledzenia zleceń i statusów" width="665" height="665" loading="eager" />
                         </picture>
                    </div>
                </div>
            </section>

            <section className="features-section">
                <div className="section-header">
                    <h2>{t('features.title')}</h2>
                    <p>{t('features.subtitle')}</p>
                </div>

                <div className="features-grid">
                    <div className="feature-card">
                        <div className="feature-icon icon-yellow">
                            <Send size={32} color="#1E293B" strokeWidth={1.5} />
                        </div>
                        <h3>{t('features.multichannel.title')}</h3>
                        <p>{t('features.multichannel.desc')}</p>
                    </div>

                    <div className="feature-card">
                        <div className="feature-icon icon-yellow">
                            <Users size={32} color="#1E293B" strokeWidth={1.5} />
                        </div>
                        <h3>{t('features.teamwork.title')}</h3>
                        <p>{t('features.teamwork.desc')}</p>
                    </div>

                    <div className="feature-card">
                        <div className="feature-icon icon-yellow">
                            <LayoutTemplate size={32} color="#1E293B" strokeWidth={1.5} />
                        </div>
                        <h3>{t('features.templateBuilder.title')}</h3>
                        <p>{t('features.templateBuilder.desc')}</p>
                    </div>

                    <div className="feature-card">
                        <div className="feature-icon icon-yellow">
                            <BarChart3 size={32} color="#1E293B" strokeWidth={1.5} />
                        </div>
                        <h3>{t('features.analytics.title')}</h3>
                        <p>{t('features.analytics.desc')}</p>
                    </div>

                    <div className="feature-card">
                        <div className="feature-icon icon-yellow">
                            <FileText size={32} color="#1E293B" strokeWidth={1.5} />
                        </div>
                        <h3>{t('features.publicForms.title')}</h3>
                        <p>{t('features.publicForms.desc')}</p>
                    </div>

                    <div className="feature-card">
                        <div className="feature-icon icon-yellow">
                            <ShieldCheck size={32} color="#1E293B" strokeWidth={1.5} />
                        </div>
                        <h3>{t('features.security.title')}</h3>
                        <p>{t('features.security.desc')}</p>
                    </div>
                </div>
            </section>

            <section className="use-cases-section">
                <div className="section-header">
                    <h2>{t('useCases.title')}</h2>
                    <p>{t('useCases.subtitle')}</p>
                </div>

                <div className="use-cases-grid">
                    <div className="use-case-card" onClick={() => navigate('/zastosowania/logistyka')}>
                        <div className="use-case-icon">
                            <Truck size={32} />
                        </div>
                        <h3>{t('useCases.scenarios.logistics.title')}</h3>
                        <p>{t('useCases.scenarios.logistics.description')}</p>
                        <div className="use-case-link">
                            {t('useCases.readMore')} <ArrowRight size={16} />
                        </div>
                    </div>

                    <div className="use-case-card" onClick={() => navigate('/zastosowania/serwis')}>
                        <div className="use-case-icon">
                            <Wrench size={32} />
                        </div>
                        <h3>{t('useCases.scenarios.service.title')}</h3>
                        <p>{t('useCases.scenarios.service.description')}</p>
                        <div className="use-case-link">
                            {t('useCases.readMore')} <ArrowRight size={16} />
                        </div>
                    </div>

                    <div className="use-case-card" onClick={() => navigate('/zastosowania/hotelarstwo')}>
                        <div className="use-case-icon">
                            <Hotel size={32} />
                        </div>
                        <h3>{t('useCases.scenarios.hospitality.title')}</h3>
                        <p>{t('useCases.scenarios.hospitality.description')}</p>
                        <div className="use-case-link">
                            {t('useCases.readMore')} <ArrowRight size={16} />
                        </div>
                    </div>

                    <div className="use-case-card" onClick={() => navigate('/zastosowania/it')}>
                        <div className="use-case-icon">
                            <Monitor size={32} />
                        </div>
                        <h3>{t('useCases.scenarios.it.title')}</h3>
                        <p>{t('useCases.scenarios.it.description')}</p>
                        <div className="use-case-link">
                            {t('useCases.readMore')} <ArrowRight size={16} />
                        </div>
                    </div>

                    <div className="use-case-card" onClick={() => navigate('/zastosowania/transport')}>
                        <div className="use-case-icon">
                            <Truck size={32} />
                        </div>
                        <h3>{t('useCases.scenarios.transport.title')}</h3>
                        <p>{t('useCases.scenarios.transport.description')}</p>
                        <div className="use-case-link">
                            {t('useCases.readMore')} <ArrowRight size={16} />
                        </div>
                    </div>

                    <div className="use-case-card" onClick={() => navigate('/zastosowania/nieruchomosci')}>
                        <div className="use-case-icon">
                            <Home size={32} />
                        </div>
                        <h3>{t('useCases.scenarios.realestate.title')}</h3>
                        <p>{t('useCases.scenarios.realestate.description')}</p>
                        <div className="use-case-link">
                            {t('useCases.readMore')} <ArrowRight size={16} />
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== CHECKLISTS / FORMS SECTION ===== */}
            <section className="features-section" id="checklisty">
                <div className="section-header">
                    <span style={{
                        display: 'inline-block',
                        background: '#FFF9C4',
                        color: '#92700A',
                        borderRadius: '999px',
                        padding: '0.3rem 1rem',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        marginBottom: '1rem',
                        border: '1px solid #F5DF4E'
                    }}>NOWOŚĆ</span>
                    <h2>Formularze i checklisty — zbieraj dane bez aplikacji</h2>
                    <p>Poza śledzeniem statusów możesz zbierać ustrukturyzowane dane od pracowników i podwykonawców — przez unikalny link, bez instalowania czegokolwiek.</p>
                </div>

                <div className="use-cases-grid" style={{ maxWidth: '1200px', margin: '0 auto 3.5rem' }}>
                    {[
                        { icon: <Car size={32} />, title: 'Protokół przyjęcia pojazdu', desc: 'Mechanik skanuje QR, wypełnia formularz: ilość paliwa, stan opon, usterki, zdjęcie karoserii. Dane trafiają od razu do systemu.' },
                        { icon: <Hotel size={32} />, title: 'Inspekcja pokoju hotelowego', desc: 'Pokojowa otwiera link, zaznacza checkboxy (sprzątanie, ręczniki, minibar), dodaje zdjęcie usterki. Recepcja widzi status na bieżąco.' },
                        { icon: <Package size={32} />, title: 'Odbiór towaru / dostawa', desc: 'Kierowca potwierdza dostawę: ilość paczek, stan opakowania, zdjęcie jako dowód, notatka o brakach. Wszystko udokumentowane.' },
                        { icon: <Wrench size={32} />, title: 'Raport serwisowy maszyny', desc: 'Serwisant wypełnia checklistę po naprawie: wykonane czynności, zużyte części, zdjęcie tabliczki. Zero papierologii.' },
                        { icon: <HardHat size={32} />, title: 'Odbiór prac budowlanych', desc: 'Inspektor zaznacza co OK / do poprawki, dodaje zdjęcia. Raport gotowy w sekundę.' },
                        { icon: <Sparkles size={32} />, title: 'Kontrola jakości sprzątania', desc: 'Supervisor sprawdza pomieszczenia, zaznacza checklist, dodaje zdjęcia gdy coś nie gra. Firma sprzątająca widzi wyniki na bieżąco.' }
                    ].map((item, i) => (
                        <div key={i} className="use-case-card" style={{ cursor: 'default', textAlign: 'left', alignItems: 'flex-start' }}>
                            <div className="use-case-icon" style={{ alignSelf: 'center' }}>{item.icon}</div>
                            <h3 style={{ textAlign: 'left', width: '100%' }}>{item.title}</h3>
                            <p style={{ textAlign: 'left' }}>{item.desc}</p>
                        </div>
                    ))}
                </div>

                {/* 3 steps box */}
                <div style={{
                    maxWidth: '900px', margin: '4rem auto 3rem',
                    background: '#F8FAFC', borderRadius: '20px', padding: '2.5rem',
                    border: '1px solid #E2E8F0'
                }}>
                    <h3 style={{ textAlign: 'center', marginBottom: '2rem', fontSize: '1.4rem', color: '#2D3748', fontWeight: 700 }}>
                        Jak to działa? 3 kroki
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '2rem' }}>
                        {[
                            { num: '1', title: 'Stwórz szablon checklisty', desc: 'Dodaj pola: tekst, liczby, checkboxy, listy wyboru, zdjęcia. Każde pole możesz oznaczyć jako wymagane.' },
                            { num: '2', title: 'Wyślij link pracownikowi', desc: 'Generujesz unikalny link lub kod QR. Pracownik klika, widzi formularz — bez logowania, bez aplikacji.' },
                            { num: '3', title: 'Zbierasz dane w panelu', desc: 'Odpowiedzi, zdjęcia i dane trafiają do Twojego panelu. Widzisz kto, kiedy i co wypełnił.' }
                        ].map((step, i) => (
                            <div key={i} style={{ textAlign: 'center' }}>
                                <div style={{
                                    width: '48px', height: '48px', background: '#F5DF4E',
                                    borderRadius: '50%', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', margin: '0 auto 1rem',
                                    color: '#1E293B', fontWeight: 800, fontSize: '1.2rem'
                                }}>{step.num}</div>
                                <h4 style={{ color: '#2D3748', fontWeight: 700, marginBottom: '0.5rem', fontSize: '1rem' }}>{step.title}</h4>
                                <p style={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.6, margin: 0 }}>{step.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Features chips */}
                <div style={{ maxWidth: '900px', margin: '0 auto 3rem' }}>
                    <h3 style={{ color: '#2D3748', fontSize: '1.2rem', fontWeight: 700, marginBottom: '1rem', textAlign: 'center' }}>
                        Co możesz zbierać w checkliście:
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
                        {[
                            'Pola tekstowe i liczbowe',
                            'Checkboxy (Tak / Nie)',
                            'Listy rozwijane z opcjami',
                            'Pola na daty i godziny',
                            'Załączniki zdjęciowe (aparat telefonu)',
                            'Pola wymagane i opcjonalne',
                            'Powiadomienia e-mail po wypełnieniu',
                            'Unikalny link dla każdego pracownika',
                        ].map((feat, i) => (
                            <div key={i} style={{
                                background: '#FFFFFF', border: '1px solid #E2E8F0',
                                borderRadius: '10px', padding: '0.7rem 1rem',
                                color: '#475569', fontSize: '0.9rem', fontWeight: 500,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                                textAlign: 'left'
                            }}>
                                <CheckCircle2 size={18} color="#10B981" strokeWidth={2.5} style={{ flexShrink: 0, marginTop: '2px' }} />
                                <span>{feat}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{ textAlign: 'center' }}>
                    <p style={{ color: '#64748B', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                        Dostępne w każdym planie — zacznij za darmo, bez karty kredytowej
                    </p>
                    <button className="btn btn-primary" onClick={handleStartClick} style={{ fontSize: '1.05rem', padding: '0.9rem 2.5rem' }}>
                        Wypróbuj formularze za darmo →
                    </button>
                </div>
            </section>
            {/* ===== END CHECKLISTS SECTION ===== */}


            <section className="how-it-works-section" id="how-it-works">

                <div className="section-header">
                    <h2>{t('howItWorks.title')}</h2>
                    <p>{t('howItWorks.subtitle')}</p>
                </div>

                <div className="steps-container">
                    <div className="step">
                        <div className="step-content">
                            <div className="step-number">01</div>
                            <h3>{t('howItWorks.step1.title')}</h3>
                            <p>{t('howItWorks.step1.desc')}</p>
                        </div>
                        <div className="step-image">
                            <picture>
                                <source srcSet="/howto-step1.webp" type="image/webp" />
                                <img src="/howto-step1.png" alt={t('howItWorks.step1.title')} width="665" height="665" loading="lazy" />
                            </picture>
                        </div>
                    </div>

                    <div className="step">
                        <div className="step-content">
                            <div className="step-number">02</div>
                            <h3>{t('howItWorks.step2.title')}</h3>
                            <p>{t('howItWorks.step2.desc')}</p>
                        </div>
                        <div className="step-image">
                            <picture>
                                <source srcSet="/howto-step2.webp" type="image/webp" />
                                <img src="/howto-step2.png" alt={t('howItWorks.step2.title')} width="665" height="665" loading="lazy" />
                            </picture>
                        </div>
                    </div>

                    <div className="step">
                        <div className="step-content">
                            <div className="step-number">03</div>
                            <h3>{t('howItWorks.step3.title')}</h3>
                            <p>{t('howItWorks.step3.desc')}</p>
                        </div>
                        <div className="step-image">
                            <picture>
                                <source srcSet="/howto-step3.webp" type="image/webp" />
                                <img src="/howto-step3.png" alt={t('howItWorks.step3.title')} width="665" height="665" loading="lazy" />
                            </picture>
                        </div>
                    </div>
                </div>
            </section>

            <section className="pricing-section" id="pricing">
                <div className="section-header">
                    <h2>{t('pricing.title')}</h2>
                    <p>{t('pricing.subtitle')}</p>
                </div>

                <div className="plans-grid">
                    {Object.entries(PLANS).map(([key, plan]) => (
                        <div key={key} className={`plan-card ${plan.popular ? 'popular' : ''}`}>
                            {plan.popular && <div className="popular-badge">Popularny</div>}
                            <h3>{plan.name}</h3>
                            <div className="plan-price">
                                <span className="amount">{plan.price}</span>
                                <span className="currency">{t('pricing.perMonth')}</span>
                            </div>
                            <div className="plan-status-count">
                                {t('pricing.statusCount', { count: plan.statusLimit === 10000 ? t('pricing.unlimited') : plan.statusLimit })}
                            </div>
                            <ul className="plan-features">
                                {plan.features.map((f, i) => <li key={i}>{f}</li>)}
                            </ul>
                            <button
                                onClick={() => document.getElementById('auth-section').scrollIntoView({ behavior: 'smooth' })}
                                className={`btn ${plan.popular ? 'btn-primary' : 'btn-secondary'}`}
                            >
                                {key === 'free' ? t('pricing.cta.free') : t('pricing.cta.choose')}
                            </button>
                        </div>
                    ))}
                </div>
            </section>

            <section className="faq-section" id="faq">
                <div className="section-header">
                    <h2>{t('faq.title')}</h2>
                    <p>{t('faq.subtitle')}</p>
                </div>

                <div className="faq-container">
                    {faqs.map((faq, index) => (
                        <div
                            key={index}
                            className={`faq-item ${openFaq === index ? 'active' : ''}`}
                            onClick={() => toggleFaq(index)}
                        >
                            <div className="faq-question">
                                <h3>{faq.question}</h3>
                                <span className="faq-toggle">{openFaq === index ? '−' : '+'}</span>
                            </div>
                            <div className="faq-answer">
                                <p>{faq.answer}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="auth-section" id="auth-section">
                <LandingAuth idSuffix="-footer" />
            </section>
            </main>

            <footer className="landing-footer">
                <div className="footer-content">
                    <div className="footer-brand">
                        <h3>sprawdzstatus.pl</h3>
                        <p>{t('footer.brandDesc')}</p>
                    </div>
                    <div className="footer-links">
                        <div className="footer-column">
                             <h4>{t('footer.product')}</h4>
                             <a href="#how-it-works" onClick={(e) => { e.preventDefault(); document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' }); }}>{t('footer.howItWorks')}</a>
                             <a href="#pricing" onClick={(e) => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }}>{t('footer.pricing')}</a>
                             <Link to="/blog">Blog</Link>
                             <Link to="/terms">{t('footer.terms')}</Link>
                             <Link to="/privacy-policy">{t('footer.privacy')}</Link>
                         </div>
                         <div className="footer-column">
                             <h4>{t('footer.help')}</h4>
                             <a href="#faq" onClick={(e) => { e.preventDefault(); document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' }); }}>{t('footer.faq')}</a>
                             <a href="mailto:kontakt@sprawdzstatus.pl">{t('footer.contact')}</a>
                         </div>
                    </div>
                </div>
                <div className="footer-bottom">
                    <p dangerouslySetInnerHTML={{ __html: t('footer.copyright', { year: `2024–${new Date().getFullYear()}` }) }}></p>
                </div>
            </footer>
        </div>
    );
};

export default Landing;
